/* ═══════════════════════════════════════════════════════════════
   The Designer — hover "+" affordances on a table
   (Phase 1 item 3, MASTERS_UNIFIED_PLAN, 2026-08-25).

   Point at a cell and two "+" buttons appear in the margin: one just
   under the table at that column's right boundary, one just left of it
   at that row's bottom boundary. Clicking either runs the FRAMEWORK's
   own addColumnAfter / addRowAfter against that cell — this file
   supplies no table logic, only where the button sits.

   DESIGN §4: portalled to <body>, position: fixed, anchored from
   getBoundingClientRect(), so a zoomed or scrolled canvas can never
   clip them. They are a shortcut, never the only path — the same two
   commands live in the table toolbar and the right-click menu (§4:
   nothing is hover-only).
   ═══════════════════════════════════════════════════════════════ */

import React from 'react';
import { createPortal } from 'react-dom';
import type { Editor } from '@tiptap/react';
import { Plus } from 'lucide-react';
import { Icon } from '../components/ui/Icon';
import { focusCellAt } from './tableCommands';
import { appLayerProps } from '../components/ui/app-layer';

interface Props {
  editor: Editor;
  canEdit: boolean;
}

interface Hot {
  cell: HTMLTableCellElement;
  table: HTMLTableElement;
}

interface Spot {
  colX: number;
  colY: number;
  rowX: number;
  rowY: number;
}

/** How far OUTSIDE the table the affordances sit. Two collisions, both found
 *  in verification, decide this geometry:
 *    · they must clear the table BODY — the framework's own column-resize
 *      handle lives on a cell's right edge, and a 32px button centred on that
 *      same point swallows the drag before ProseMirror ever sees it;
 *    · the column "+" therefore rides the table's BOTTOM edge, not its top:
 *      the top belongs to the floating table toolbar, which anchors 8px above
 *      the table and would sit under the button. */
const GRIP_OFFSET = 18;

function measure(hot: Hot): Spot {
  const c = hot.cell.getBoundingClientRect();
  const t = hot.table.getBoundingClientRect();
  return {
    colX: c.right,
    colY: t.bottom + GRIP_OFFSET,
    rowX: t.left - GRIP_OFFSET,
    rowY: c.bottom,
  };
}

export function TableGrips({ editor, canEdit }: Props) {
  const [hot, setHot] = React.useState<Hot | null>(null);
  const [spot, setSpot] = React.useState<Spot | null>(null);

  // One document-level listener rather than per-table handlers: the tables
  // are ProseMirror's DOM, re-created on every structural edit, and a
  // listener bound to a node that no longer exists is a silent dead
  // affordance.
  React.useEffect(() => {
    if (!canEdit) {
      setHot(null);
      return;
    }
    const onMove = (e: MouseEvent) => {
      const target = e.target as HTMLElement | null;
      if (!target || typeof target.closest !== 'function') return;
      // Over one of our own buttons — keep showing exactly what is showing.
      if (target.closest('.dsg-tbl-grips')) return;
      const cell = target.closest<HTMLTableCellElement>('td,th');
      const table = cell?.closest<HTMLTableElement>('table.dsg-real-table') || null;
      if (!cell || !table || !editor.view.dom.contains(table)) {
        setHot((cur) => (cur ? null : cur));
        return;
      }
      setHot((cur) => (cur && cur.cell === cell ? cur : { cell, table }));
    };
    document.addEventListener('mousemove', onMove, true);
    return () => document.removeEventListener('mousemove', onMove, true);
  }, [editor, canEdit]);

  // Re-measure whenever the hovered cell changes, and follow scroll/resize —
  // the buttons are fixed-positioned, so their anchor has to be re-read.
  React.useLayoutEffect(() => {
    if (!hot) {
      setSpot(null);
      return;
    }
    const place = () => {
      if (!document.contains(hot.cell)) {
        setHot(null);
        return;
      }
      setSpot(measure(hot));
    };
    place();
    window.addEventListener('resize', place);
    window.addEventListener('scroll', place, true);
    return () => {
      window.removeEventListener('resize', place);
      window.removeEventListener('scroll', place, true);
    };
  }, [hot]);

  if (!hot || !spot || !canEdit) return null;

  // Keep the editor's selection/focus while the button is pressed — the
  // grips live outside the contenteditable (same reason the table toolbar
  // does this).
  const keepFocus = (e: React.MouseEvent) => e.preventDefault();

  const at = (cmd: 'addRowAfter' | 'addColumnAfter') => () => {
    const pos = editor.view.posAtDOM(hot.cell, 0);
    // posAtDOM lands INSIDE the cell; step back to the cell node itself.
    const $pos = editor.state.doc.resolve(pos);
    const cellPos = $pos.depth > 0 ? $pos.before($pos.depth) : pos;
    focusCellAt(editor, cellPos);
    editor.chain().focus()[cmd]().run();
  };

  return createPortal(
    <div {...appLayerProps} className="dsg-tbl-grips" aria-hidden={false}>
      <button
        type="button"
        className="dsg-tbl-grip"
        style={{ left: spot.colX, top: spot.colY }}
        onMouseDown={keepFocus}
        onClick={at('addColumnAfter')}
        aria-label="Insert column right"
        title="Insert column right"
      >
        <span className="dsg-tbl-grip-dot">
          <Icon icon={Plus} em={0.9231} aria-hidden="true" />
        </span>
      </button>
      <button
        type="button"
        className="dsg-tbl-grip"
        style={{ left: spot.rowX, top: spot.rowY }}
        onMouseDown={keepFocus}
        onClick={at('addRowAfter')}
        aria-label="Insert row below"
        title="Insert row below"
      >
        <span className="dsg-tbl-grip-dot">
          <Icon icon={Plus} em={0.9231} aria-hidden="true" />
        </span>
      </button>
    </div>,
    document.body
  );
}
